import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  useAddToBlocklist,
  useAdminUsers,
  useBlocklist,
  useRemoveFromBlocklist,
  useUpdateUser,
} from '../../api/queries'
import type { User, UserStatus } from '../../api/types'
import { Dialog } from '../../components/Dialog'
import { ErrorNotice } from '../../components/ErrorNotice'
import { Badge, Button, Field, Input, Loading, PageTitle, Segmented, Switch } from '../../components/ui'
import { cn } from '../../lib/cn'
import { fmtDate } from '../../lib/time'
import { useSession } from '../../session'

/**
 * Экраны 5.4, 5.6, 5.9, 5.10 — сотрудники и права.
 *
 * Здесь живут три разных действия, и путать их нельзя.
 *   Право на повторы — персональная привилегия, не роль.
 *   Блокировка — наказание: смотреть можно, бронировать нет.
 *   Деактивация — факт ухода из компании: войти нельзя, история остаётся.
 *
 * Снять права с последнего администратора нельзя. Проверка на сервере — интерфейс
 * только объясняет отказ; спрятанная кнопка защитой не является.
 */

const STATUS_LABEL: Record<UserStatus, string> = {
  pending: 'Ожидает первого входа',
  active: 'Работает',
  blocked: 'Заблокирован',
  deactivated: 'Уволен',
}

export function AdminUsersPage() {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState<'all' | UserStatus>('all')

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(timer)
  }, [query])

  const users = useAdminUsers(debounced, status === 'all' ? undefined : status)
  const update = useUpdateUser()
  const { user: me } = useSession()

  const [statusChange, setStatusChange] = useState<{ user: User; next: UserStatus } | null>(null)

  return (
    <>
      <PageTitle>Сотрудники</PageTitle>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          className="max-w-xs"
          aria-label="Поиск сотрудника"
          placeholder="Имя, фамилия или адрес"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Segmented
          label="Статус сотрудника"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'Все' },
            { value: 'active', label: 'Работают' },
            { value: 'pending', label: 'Ожидают' },
            { value: 'blocked', label: 'Заблокированы' },
            { value: 'deactivated', label: 'Уволены' },
          ]}
        />
      </div>

      {update.error && <ErrorNotice error={update.error} className="mb-4" />}
      {users.error && <ErrorNotice error={users.error} className="mb-4" />}
      {users.isLoading && <Loading />}

      <div className="card divide-y divide-line overflow-hidden">
        {(users.data ?? []).map((person) => {
          const isMe = person.id === me.id
          return (
            <div key={person.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {person.last_name} {person.first_name}
                    {!person.last_name && !person.first_name && (
                      <span className="text-muted">Имя не заполнено</span>
                    )}
                  </span>
                  {isMe && <Badge tone="accent">Это вы</Badge>}
                  <Badge tone={person.status === 'active' ? 'free' : person.status === 'blocked' ? 'busy' : 'neutral'}>
                    {STATUS_LABEL[person.status]}
                  </Badge>
                </div>
                <p className="tnum mt-0.5 text-sm text-muted">
                  {person.email}
                  {person.position && <span className="not-italic"> · {person.position}</span>}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <ToggleCell
                  label="Повторы"
                  title="Право создавать повторяющиеся серии. Отзыв не трогает уже созданные: они доигрывают до конца."
                  checked={person.can_recur}
                  onChange={(next) => update.mutate({ id: person.id, can_recur: next })}
                />
                <ToggleCell
                  label="Админ"
                  title={
                    isMe
                      ? 'Снять права с себя нельзя: если администратор один, доступ к настройкам теряется навсегда.'
                      : 'Доступ к разделу «Управление».'
                  }
                  checked={person.is_admin}
                  disabled={isMe && person.is_admin}
                  onChange={(next) => update.mutate({ id: person.id, is_admin: next })}
                />

                <StatusMenu
                  person={person}
                  isMe={isMe}
                  onPick={(next) => setStatusChange({ user: person, next })}
                />
              </div>
            </div>
          )
        })}

        {users.data && users.data.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-muted">
            Никого не нашли. Сотрудники появляются здесь сами — при первом входе или когда
            их зовут на встречу по адресу.
          </p>
        )}
      </div>

      <BlocklistSection />

      {statusChange && (
        <StatusDialog
          person={statusChange.user}
          next={statusChange.next}
          onClose={() => setStatusChange(null)}
        />
      )}
    </>
  )
}

function ToggleCell({
  label,
  title,
  checked,
  onChange,
  disabled,
}: {
  label: string
  title: string
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className="flex items-center gap-2" title={title}>
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={label} title={title} />
      <span className="text-sm text-muted">{label}</span>
    </label>
  )
}

function StatusMenu({
  person,
  isMe,
  onPick,
}: {
  person: User
  isMe: boolean
  onPick: (next: UserStatus) => void
}) {
  return (
    <div className="flex gap-2">
      {person.status !== 'blocked' && person.status !== 'deactivated' && (
        <Button
          size="sm"
          variant="ghost"
          disabled={isMe}
          title={isMe ? 'Заблокировать самого себя нельзя' : undefined}
          onClick={() => onPick('blocked')}
        >
          Заблокировать
        </Button>
      )}
      {person.status === 'blocked' && (
        <Button size="sm" variant="ghost" onClick={() => onPick('active')}>
          Разблокировать
        </Button>
      )}
      {person.status !== 'deactivated' ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={isMe}
          title={isMe ? 'Деактивировать себя нельзя' : 'Сотрудник уволился'}
          onClick={() => onPick('deactivated')}
        >
          Уволен
        </Button>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => onPick('active')}>
          Вернуть
        </Button>
      )}
    </div>
  )
}

/**
 * Развилка 7: что происходит с бронями заблокированного или уволенного.
 * Спрашиваем всегда; участников сервер уведомляет в любом случае — тихо исчезнувшая
 * встреча плоха для тех, кого позвали, а не только для владельца брони.
 */
function StatusDialog({
  person,
  next,
  onClose,
}: {
  person: User
  next: UserStatus
  onClose: () => void
}) {
  const update = useUpdateUser()
  const [futureBookings, setFutureBookings] = useState<'keep' | 'cancel'>('keep')
  const [reason, setReason] = useState('')

  const restricting = next === 'blocked' || next === 'deactivated'
  const lastAdmin = update.error instanceof ApiError && update.error.code === 'last_admin'

  return (
    <Dialog
      open
      onClose={onClose}
      title={titleFor(next, person)}
      description={descriptionFor(next)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={restricting ? 'danger' : 'primary'}
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  id: person.id,
                  status: next,
                  future_bookings: restricting ? futureBookings : undefined,
                  reason: reason.trim() || undefined,
                },
                { onSuccess: onClose },
              )
            }
          >
            {restricting ? 'Подтвердить' : 'Вернуть доступ'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {restricting && (
          <Field label="Что делать с будущими бронями" group>
            <div className="flex flex-col gap-2">
              <Choice
                name="future-bookings"
                checked={futureBookings === 'keep'}
                onChange={() => setFutureBookings('keep')}
                label="Оставить"
                hint="Встречи состоятся, комнаты остаются занятыми."
              />
              <Choice
                name="future-bookings"
                checked={futureBookings === 'cancel'}
                onChange={() => setFutureBookings('cancel')}
                label="Отменить и уведомить участников"
                hint="Комнаты освободятся, участники получат письмо с причиной."
              />
            </div>
          </Field>
        )}

        {/*
          Про обязательность сказано в подписи, а не только в отказе сервера: при блокировке
          причина обязательна — она переживает саму блокировку и объясняет её следующему
          администратору. При деактивации не нужна: увольнение объяснять некому.
        */}
        {restricting && (
          <Field
            label={next === 'blocked' ? 'Причина (обязательно)' : 'Причина'}
            hint={
              next === 'blocked'
                ? 'Останется в чёрном списке рядом с адресом и попадёт в письмо участникам, если брони отменяются.'
                : 'Попадёт в письмо участникам, если брони отменяются, и останется в журнале.'
            }
          >
            <Input
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={next === 'deactivated' ? 'Например: уволился 8 августа' : 'Например: систематический захват комнат'}
            />
          </Field>
        )}

        {lastAdmin ? (
          <div className="rounded-card border border-state-busy/40 bg-state-busy/10 px-4 py-3 text-sm">
            <p className="font-medium">Это последний администратор</p>
            <p className="mt-1 text-fg/80">
              Снять права или заблокировать его нельзя: доступ к настройкам потеряется совсем
              и починить это можно будет только руками в базе. Сначала назначьте второго
              администратора.
            </p>
          </div>
        ) : (
          update.error && <ErrorNotice error={update.error} />
        )}
      </div>
    </Dialog>
  )
}

function titleFor(next: UserStatus, person: User): string {
  const name = `${person.first_name} ${person.last_name}`.trim() || person.email
  if (next === 'blocked') return `Заблокировать ${name}?`
  if (next === 'deactivated') return `Отметить, что ${name} уволился?`
  return `Вернуть доступ: ${name}`
}

function descriptionFor(next: UserStatus): string {
  if (next === 'blocked')
    return 'Расписание он по-прежнему увидит, а бронировать не сможет. Блокировка привязана к адресу почты: удалить учётную запись и зарегистрироваться заново тем же адресом не поможет.'
  if (next === 'deactivated')
    return 'Пропадёт из поиска участников и не сможет войти. История его встреч сохранится. Это факт ухода, а не наказание.'
  return 'Человек снова сможет входить и бронировать переговорные.'
}

function Choice({
  checked,
  onChange,
  label,
  hint,
  name,
}: {
  checked: boolean
  onChange: () => void
  label: string
  hint: string
  /** Общее имя переключателей одной развилки: без него стрелки на клавиатуре не работают. */
  name: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition',
        checked ? 'border-accent bg-accent-soft/40' : 'border-line hover:bg-surface-2',
      )}
    >
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-sm text-muted">{hint}</span>
      </span>
    </label>
  )
}

/**
 * Чёрный список адресов.
 *
 * Отдельно от статуса учётной записи, потому что привязан к почте: сюда можно внести
 * адрес, у которого учётной записи ещё нет вовсе, и человек не пройдёт даже регистрацию.
 */
function BlocklistSection() {
  const blocklist = useBlocklist()
  const add = useAddToBlocklist()
  const remove = useRemoveFromBlocklist()

  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('')

  return (
    <section className="mt-8">
      <h2 className="mb-1 text-lg font-semibold">Чёрный список адресов</h2>
      <p className="mb-4 hint max-w-2xl">
        Запрет привязан к адресу почты, а не к учётной записи: иначе заблокированный удалил бы
        аккаунт, зарегистрировался тем же адресом заново и обошёл запрет.
      </p>

      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (!email.trim()) return
          add.mutate(
            { email: email.trim().toLowerCase(), reason: reason.trim() || undefined },
            {
              onSuccess: () => {
                setEmail('')
                setReason('')
              },
            },
          )
        }}
      >
        <Field label="Адрес">
          <Input
            type="email"
            className="tnum"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        {/* Сервер причину требует (422 validation_failed), значит и подпись обязана
            сказать это заранее, а не после отказа. */}
        <Field label="Причина (обязательно)">
          <Input
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Например: рассылал спам через приглашения"
          />
        </Field>
        <Button type="submit" variant="ghost" loading={add.isPending}>
          Добавить
        </Button>
      </form>

      {(add.error || remove.error || blocklist.error) && (
        <ErrorNotice error={add.error ?? remove.error ?? blocklist.error} className="mb-4" />
      )}

      <div className="card divide-y divide-line overflow-hidden">
        {(blocklist.data ?? []).map((entry) => (
          <div key={entry.email} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="tnum font-medium">{entry.email}</p>
              <p className="text-sm text-muted">
                {entry.reason || 'Причина не указана'} · с {fmtDate(entry.created_at)}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              loading={remove.isPending}
              onClick={() => remove.mutate(entry.email)}
            >
              Убрать
            </Button>
          </div>
        ))}
        {blocklist.data && blocklist.data.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted">Список пуст.</p>
        )}
      </div>
    </section>
  )
}
